import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Loader2 } from 'lucide-react'

interface RejectUSModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
  isLoading: boolean
  userStoryTitle: string
}

export function RejectUSModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  userStoryTitle,
}: RejectUSModalProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')

  if (!isOpen) return null

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason.trim())
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setReason('')
      onClose()
    }
  }

  const isValid = reason.trim().length > 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={handleClose}
    >
      <div
        className="flex w-[480px] max-w-[90vw] flex-col overflow-hidden rounded-xl bg-white shadow-xl animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {t('us.rejectTitle')}
          </h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground disabled:opacity-50"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4 p-6">
          <p className="text-sm text-foreground-muted">
            {t('us.rejectDescription', { title: userStoryTitle })}
          </p>

          <div className="flex flex-col gap-2">
            <label htmlFor="rejection-reason" className="text-sm font-medium text-foreground">
              {t('us.rejectReasonLabel')}
            </label>
            <textarea
              id="rejection-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('us.rejectReasonPlaceholder')}
              disabled={isLoading}
              rows={4}
              className="w-full resize-none rounded-lg border border-border bg-background-secondary px-4 py-3 text-sm placeholder:text-foreground-subtle focus:border-accent focus:outline-none disabled:opacity-50"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
            className="flex items-center gap-2 rounded-lg bg-destructive px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-destructive/90 disabled:opacity-50"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('us.rejectConfirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
